import { HttpResponse } from "@/fetchInterceptor/AxiosMethod";
import { DateTimePeriodDto, OutlookAppointmentRecurrenceDto, OutlookRecipientDto } from "@/out-look/dto";
import { addNotification, resetNotification } from "@/redux/slices/notification/NotificationSlice";
import { getCategorization, getOrgInfo, updateOrganizer, updateOutlookInfo, updateRecurringOfOutlook, updateTimeOfOutlook } from "@/redux/slices/organizations/OrganizationSlice";
import {
  addAttendee,
  deleteSelectWhenFromOutlook,
  fetchAndUpdateResources,
  updateAllResourcesDataInSelectedChangeRsOutlook,
  updateFilterResource,
  updateLoadingSelectedRs,
  updateMainAttendee,
  updateResourceSelectedFromOutlook,
  updateResourcesDataInSelectedChangeRsOutlook,
} from "@/redux/slices/resources/ResourceSlice";
import { getSettingOrgInfo, setTheme } from "@/redux/slices/settings/SettingSlice";
import {
  IConvertDateTimeToUTCReq,
  IConvertDateTimeToUTCResp,
  IFetchRequest,
  IFetchResourceRequest,
  IFilterResource,
  IOrganizationService,
  IResourceService,
  OrganizationInfo,
  OrganizationService,
  Organizer,
  ResourceInfo,
  ResourceService,
} from "@/services";
import { AttendeeInfo } from "@/services/attendee-service/entity/AttendeeEntity";
import { NotificationInfo } from "@/services/notification-service/entity/NotificationEntity";
import { EMessageType } from "@/services/notification-service/entity/NotificationEnum";
import { OutLookInfo } from "@/services/organization-service/entity/OutLookEntity";
import { Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import ResourceFinder from ".";

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    getOrgInfo: (outlookUid: string, isReOpen?: boolean, isRecurringMaster?: boolean) => {
      dispatch(getOrgInfo({ outlookUid, isReOpen, isRecurringMaster }));
    },
    getCategorization: (tenantId: string, orgId: string) => {
      dispatch(getCategorization({ orgId, tenantId }));
    },
    updateOutlookInfo: (outlookInfo: OutLookInfo) => {
      dispatch(updateOutlookInfo(outlookInfo));
    },
    updateOrganizer: (org: Organizer) => {
      dispatch(updateOrganizer(org));
    },
    updateTimeOfOutlook: (time: DateTimePeriodDto) => {
      dispatch(updateTimeOfOutlook(time));
    },
    updateResourceSelectedFromOutlook: (resources: OutlookRecipientDto[]) => {
      dispatch(updateResourceSelectedFromOutlook(resources));
    },
    updateRecurringOfOutlook: (recurringInfo: OutlookAppointmentRecurrenceDto) => {
      dispatch(updateRecurringOfOutlook(recurringInfo));
    },
    setTheme: (themeId: string) => {
      dispatch(setTheme(themeId));
    },
    getSettingOrgInfo: (payload: IFetchRequest) => {
      dispatch(getSettingOrgInfo(payload));
    },
    deleteSelectWhenFromOutlook: (resourceEmails: string[]) => {
      dispatch(deleteSelectWhenFromOutlook(resourceEmails));
    },
    getResourcesDataForSelected: async (request: IFetchResourceRequest, outlookRecipientDto: OutlookRecipientDto[], isRecurring?: boolean, orgInfo?: OrganizationInfo): Promise<void> => {
      const resourceServiceImpl: IResourceService = new ResourceService();
      const response = await resourceServiceImpl.fetchResourcesByEmails(request);

      const emailsToCheck = outlookRecipientDto.length ? outlookRecipientDto.map((a) => a.Email) : [];

      const emailSet = new Set(response.data.map((item) => item.email));

      const attendees = emailsToCheck.filter((email) => !emailSet.has(email));

      if (attendees) {
        attendees.forEach((item) => {
          const att = outlookRecipientDto.find((a) => a.Email.toLowerCase() === item);

          const newAtt = new AttendeeInfo({
            email: att?.Email,
            displayName: att?.Name,
            name: att?.Name,
          });
          dispatch(addAttendee(newAtt));
        });
      }

      if (!response.error) {
        const filterResources = (resources: ResourceInfo[], hasDeclines: boolean) =>
          resources.filter((item) => !!item.permission?.hasIconPermission(isRecurring, request.requestBody.MeetingInfos?.length || 0) === hasDeclines);

        const { data: resources } = response;
        const rsCorrect = filterResources(resources, false);
        const rsInCorrect = filterResources(resources, true);

        dispatch(
          updateResourcesDataInSelectedChangeRsOutlook({
            resources: rsCorrect,
            orgInfo: orgInfo || new OrganizationInfo(),
          })
        );
        dispatch(fetchAndUpdateResources({ resources, orgInfo: orgInfo || new OrganizationInfo() }));

        if (rsInCorrect.length) {
          resourceServiceImpl.toggleRemoveResources(rsInCorrect);
          const notifyInfos = rsInCorrect.map((item) => {
            //resourceServiceImpl.toggleRemoveResource(item);
            return new NotificationInfo({
              messageType: EMessageType.ResourceConflict,
              resourceInfo: item,
              isRecurring,
            });
          });

          dispatch(addNotification(notifyInfos));
        }
      }
    },
    getAllResourcesDataInSelected: async (request: IFetchResourceRequest, isRecurring?: boolean): Promise<void> => {
      const resourceServiceImpl: IResourceService = new ResourceService();
      dispatch(updateLoadingSelectedRs(true));
      const response = await resourceServiceImpl.fetchResourcesByEmails(request);
      if (!response.error) {
        const rsInCorrect: ResourceInfo[] = response.data.filter((item: ResourceInfo) => !!item.permission?.hasIconPermission(isRecurring, request.requestBody.MeetingInfos?.length || 0));
        const rsCorrect: ResourceInfo[] = response.data.filter((item: ResourceInfo) => !item.permission?.hasIconPermission(isRecurring, request.requestBody.MeetingInfos?.length || 0));
        dispatch(updateAllResourcesDataInSelectedChangeRsOutlook(rsCorrect));
        if (rsInCorrect.length) {
          resourceServiceImpl.toggleRemoveResources(rsInCorrect);
          const notifyInfos: NotificationInfo[] = rsInCorrect.map((item: ResourceInfo) => {
            return new NotificationInfo({
              messageType: EMessageType.ResourceConflict,
              resourceInfo: item,
              isRecurring,
            });
          });
          dispatch(addNotification(notifyInfos));
        }
      }
      dispatch(updateLoadingSelectedRs(false));
    },
    addInitAttendee: (payload: AttendeeInfo) => {
      dispatch(updateMainAttendee(payload));
    },
    onConvertDateTimeToUTC: (req: IConvertDateTimeToUTCReq): Promise<HttpResponse<IConvertDateTimeToUTCResp>> => {
      const orgServiceImpl: IOrganizationService = new OrganizationService();
      return orgServiceImpl.convertDateTimeToUTC(req).then((resp) => resp);
    },
    onResetNotification: () => {
      dispatch(resetNotification());
    },
    updateFilterResource: (filterResource: IFilterResource) => {
      dispatch(updateFilterResource(filterResource));
    },
  };
};

export default connect(null, mapDispatchToProps)(ResourceFinder);
